import React, { useEffect, useMemo, useState } from 'react';
import { urlApiProd } from '../utiles';
import { ThOrden, ordenarFilas, useOrdenTabla } from './ordenTabla';

const ESTADOS_BOOKING = { 0: 'Pendiente', 1: 'Solicitado', 2: 'Confirmado', 3: 'Embarcado' };
const ESTADOS_PRODUCCION = ['Sin producción', 'Parcial', 'Producido completo'];
const POR_PAGINA = 15;
const MESES = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

const fmtFecha = (iso) => {
    if (!iso) return '—';
    const [anio, mes, dia] = iso.split('-');
    return `${dia}/${mes}/${anio.slice(2)}`;
};

const mesDe = (iso) => (iso ? iso.slice(0, 7) : '');

const etiquetaMes = (anioMes) => {
    const [anio, mes] = anioMes.split('-');
    return `${MESES[Number(mes) - 1]} ${anio}`;
};

const porcentaje = (valor, total) => (total > 0 ? `${Math.min((valor / total) * 100, 100)}%` : '0%');

const fmtM3 = (valor) => Number(valor).toLocaleString('es-CL', { maximumFractionDigits: 1 });

const listaPi = (pis) => pis.map((p) => p.id_pi || `PI #${p.id_proforma}`);

// Valores por los que se ordena cada columna (Estado de producción por su orden lógico)
const ACCESORES = {
    oc: (f) => f.id_oc,
    proveedor: (f) => f.proveedor,
    producto: (f) => f.producto,
    fecha: (f) => f.fecha_produccion_obj,
    avance: (f) => (f.m3_total > 0 ? f.m3_producido / f.m3_total : null),
    sinBooking: (f) => f.saldo_sin_booking,
    pi: (f) => listaPi(f.pis).join(' '),
    estado: (f) => ESTADOS_PRODUCCION.indexOf(f.estado_produccion),
};

const BarraProduccion =({ producido, total }) => (
    <>
        <div className="ce-bar">
            <div className="emb" style={{ width: porcentaje(producido, total) }} />
            <div className="sin" style={{ width: porcentaje(Math.max(total - producido, 0), total) }} />
        </div>
        <div className="ce-muted ce-mono mt-1">
            {fmtM3(producido)} de {fmtM3(total)} m³
        </div>
    </>
);

const SubTablaBookings = ({ fila }) => {
    const pendientes = fila.saldo_sin_booking || 0;
    return (
        <div className="ce-sub">
            <table>
                <thead>
                    <tr>
                        <th>Booking</th>
                        <th>FW / Carrier</th>
                        <th>POL → POD</th>
                        <th>ETD</th>
                        <th>HC de esta OC</th>
                        <th>PI · Cliente</th>
                        <th>Reserva</th>
                    </tr>
                </thead>
                <tbody>
                    {fila.bookings.map((b) => (
                        <tr key={b.id_solicitud}>
                            <td className="ce-mono">{b.nro_booking || `#${b.id_solicitud}`}</td>
                            <td>{[b.ffw, b.carrier].filter(Boolean).join(' · ') || '—'}</td>
                            <td>{b.pol || '—'} → {b.pod || '—'}</td>
                            <td className="ce-mono">{fmtFecha(b.etd)}</td>
                            <td className="ce-mono">{b.hc_oc} de {b.hc_booking}</td>
                            <td>
                                {b.pi_cliente.length > 0
                                    ? b.pi_cliente.map((pc) => [pc.id_pi, pc.cliente].filter(Boolean).join(' · ')).join(', ')
                                    : '—'}
                            </td>
                            <td>
                                <span className={`ce-pill ${b.estado === 2 || b.estado === 3 ? 'ce-pill--confirmado' : 'ce-pill--neutro'}`}>
                                    {ESTADOS_BOOKING[b.estado] ?? 'Sin estado'}
                                </span>
                            </td>
                        </tr>
                    ))}
                    {pendientes > 0 && (
                        <tr>
                            <td className="ce-mono">Por solicitar</td>
                            <td>—</td>
                            <td>—</td>
                            <td className="ce-mono">{fmtFecha(fila.fecha_produccion_obj)}</td>
                            <td className="ce-mono">{pendientes} pendientes</td>
                            <td>
                                {fila.pis.length > 0
                                    ? fila.pis.map((p) => [p.id_pi || `PI #${p.id_proforma}`, p.cliente].filter(Boolean).join(' · ')).join(', ')
                                    : '—'}
                            </td>
                            <td><span className="ce-pill ce-pill--sin">Sin booking</span></td>
                        </tr>
                    )}
                </tbody>
            </table>
        </div>
    );
};

const PlanificacionOC = () => {
    const [filas, setFilas] = useState([]);
    const [cargando, setCargando] = useState(true);
    const [error, setError] = useState('');
    const [expandidas, setExpandidas] = useState({});
    const [busqueda, setBusqueda] = useState('');
    const [proveedor, setProveedor] = useState('');
    const [mes, setMes] = useState('');
    const [estado, setEstado] = useState('');
    const [pagina, setPagina] = useState(1);
    const [orden, alternarOrden] = useOrdenTabla();

    // Al cambiar un filtro o el orden se vuelve a la primera página
    useEffect(() => {
        setPagina(1);
    }, [busqueda, proveedor, mes, estado, orden]);

    useEffect(() => {
        let activo = true;
        const cargar = async () => {
            try {
                const token = localStorage.getItem('token');
                const respuesta = await fetch(`${urlApiProd}/controlembarques/planificacion-oc`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                if (!respuesta.ok) throw new Error('No se pudo cargar la planificación de OC');
                const datos = await respuesta.json();
                if (activo) setFilas(datos);
            } catch (e) {
                if (activo) setError(e.message);
            } finally {
                if (activo) setCargando(false);
            }
        };
        cargar();
        return () => { activo = false; };
    }, []);

    const opciones = useMemo(() => {
        const unicos = (valores) => [...new Set(valores.filter(Boolean))].sort();
        return {
            proveedores: unicos(filas.map((f) => f.proveedor)),
            meses: unicos(filas.map((f) => mesDe(f.fecha_produccion_obj))),
        };
    }, [filas]);

    const filtradas = useMemo(() => {
        const q = busqueda.trim().toLowerCase();
        return filas.filter((f) => {
            if (proveedor && f.proveedor !== proveedor) return false;
            if (mes && mesDe(f.fecha_produccion_obj) !== mes) return false;
            if (estado && f.estado_produccion !== estado) return false;
            if (!q) return true;
            const texto = [
                f.id_oc, f.proveedor, f.producto,
                ...listaPi(f.pis),
                ...f.bookings.map((b) => b.nro_booking),
            ].filter(Boolean).join(' ').toLowerCase();
            return texto.includes(q);
        });
    }, [filas, busqueda, proveedor, mes, estado]);

    const ordenadas = useMemo(() => ordenarFilas(filtradas, orden, ACCESORES), [filtradas, orden]);

    const totalPaginas = Math.max(1, Math.ceil(ordenadas.length / POR_PAGINA));
    const paginaActual = Math.min(pagina, totalPaginas);
    const desde = (paginaActual - 1) * POR_PAGINA;
    const visibles = ordenadas.slice(desde, desde + POR_PAGINA);

    const alternar = (id) => setExpandidas((actual) => ({ ...actual, [id]: !actual[id] }));

    return (
        <>
            <div className="ce-filtros">
                <label className="ce-filtro ce-filtro-buscar">
                    Buscar OC, PI, proveedor, producto o booking
                    <input
                        type="text"
                        placeholder="Ej: REAL 4738-26, WTS 54-26"
                        value={busqueda}
                        onChange={(e) => setBusqueda(e.target.value)}
                    />
                </label>
                <label className="ce-filtro">
                    Proveedor
                    <select value={proveedor} onChange={(e) => setProveedor(e.target.value)}>
                        <option value="">Todos</option>
                        {opciones.proveedores.map((p) => <option key={p} value={p}>{p}</option>)}
                    </select>
                </label>
                <label className="ce-filtro">
                    Mes producción
                    <select value={mes} onChange={(e) => setMes(e.target.value)}>
                        <option value="">Todos</option>
                        {opciones.meses.map((m) => <option key={m} value={m}>{etiquetaMes(m)}</option>)}
                    </select>
                </label>
                <label className="ce-filtro">
                    Estado
                    <select value={estado} onChange={(e) => setEstado(e.target.value)}>
                        <option value="">Todos</option>
                        {ESTADOS_PRODUCCION.map((e) => <option key={e} value={e}>{e}</option>)}
                    </select>
                </label>
                <div className="ce-leyenda">
                    <span className="emb">Producido</span>
                    <span className="sin">Por producir</span>
                </div>
            </div>

            {cargando && <div className="ce-muted py-3">Cargando planificación…</div>}
            {error && <div className="alert alert-danger">{error}</div>}

            {!cargando && !error && (
                <div className="table-responsive">
                    <table className="ce-tabla">
                        <thead>
                            <tr>
                                <ThOrden clave="oc" orden={orden} alternarOrden={alternarOrden}>OC</ThOrden>
                                <ThOrden clave="proveedor" orden={orden} alternarOrden={alternarOrden}>Proveedor</ThOrden>
                                <ThOrden clave="producto" orden={orden} alternarOrden={alternarOrden}>Producto</ThOrden>
                                <ThOrden clave="fecha" orden={orden} alternarOrden={alternarOrden}>Fecha producción obj.</ThOrden>
                                <ThOrden clave="avance" orden={orden} alternarOrden={alternarOrden}>Avance de producción</ThOrden>
                                <ThOrden clave="sinBooking" orden={orden} alternarOrden={alternarOrden}>Carga sin booking</ThOrden>
                                <ThOrden clave="pi" orden={orden} alternarOrden={alternarOrden}>Número PI</ThOrden>
                                <ThOrden clave="estado" orden={orden} alternarOrden={alternarOrden}>Estado</ThOrden>
                            </tr>
                        </thead>
                        <tbody>
                            {visibles.map((fila) => {
                                const tieneDetalle = fila.bookings.length > 0 || fila.saldo_sin_booking > 0;
                                const abierta = !!expandidas[fila.id_ordencompra];
                                return (
                                    <React.Fragment key={fila.id_ordencompra}>
                                        <tr
                                            onClick={tieneDetalle ? () => alternar(fila.id_ordencompra) : undefined}
                                            style={tieneDetalle ? { cursor: 'pointer' } : undefined}
                                        >
                                            <td className="ce-mono">
                                                {tieneDetalle ? `${abierta ? '▾' : '▸'} ` : ''}{fila.id_oc}
                                            </td>
                                            <td>{fila.proveedor || '—'}</td>
                                            <td>{fila.producto || '—'}</td>
                                            <td className="ce-mono">{fmtFecha(fila.fecha_produccion_obj)}</td>
                                            <td style={{ minWidth: 200 }}>
                                                <BarraProduccion producido={fila.m3_producido} total={fila.m3_total} />
                                            </td>
                                            <td
                                                className="ce-mono"
                                                style={{ color: fila.saldo_sin_booking ? '#a3322a' : '#1d2c25', fontWeight: 700 }}
                                            >
                                                {fila.saldo_sin_booking === null ? '—' : `${fila.saldo_sin_booking} HC`}
                                            </td>
                                            <td className="ce-mono">
                                                {fila.pis.length > 0 ? listaPi(fila.pis).join(' · ') : 'Sin PI'}
                                            </td>
                                            <td>{fila.estado_produccion}</td>
                                        </tr>
                                        {abierta && (
                                            <tr>
                                                <td colSpan={8} style={{ borderBottom: 0, padding: 0 }}>
                                                    <SubTablaBookings fila={fila} />
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                            {filtradas.length === 0 && (
                                <tr>
                                    <td colSpan={8} className="ce-muted text-center py-4">
                                        No hay OC que coincidan con los filtros.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            )}
            {!cargando && !error && filtradas.length > 0 && (
                <div className="ce-paginacion">
                    <span className="ce-muted">
                        {desde + 1}–{desde + visibles.length} de {filtradas.length} OC
                    </span>
                    <div className="ce-paginacion-botones">
                        <button
                            type="button"
                            className="ui-btn ui-btn-secondary"
                            disabled={paginaActual === 1}
                            onClick={() => setPagina(paginaActual - 1)}
                        >
                            Anterior
                        </button>
                        <span className="ce-muted">Página {paginaActual} de {totalPaginas}</span>
                        <button
                            type="button"
                            className="ui-btn ui-btn-secondary"
                            disabled={paginaActual === totalPaginas}
                            onClick={() => setPagina(paginaActual + 1)}
                        >
                            Siguiente
                        </button>
                    </div>
                </div>
            )}
            <div className="ce-nota">
                Se listan todas las OC. El avance de producción compara los m³ de las NF de inventario ligadas a la OC con
                los m³ de la OC. La carga sin booking son los HC de la OC que aún no están asignados a un booking; las OC sin HC
                definido no muestran saldo. Cada fila de booking muestra solo los HC asignados a esta OC.
            </div>
        </>
    );
};

export default PlanificacionOC;
